import { EmployeeInfo } from "@/modules/types/employee/employee";
import { create } from "zustand";
import { Degree } from "../types/employee/education";
import { Experience } from "../types/employee/experience";
import { Certificate } from "../types/employee/certificate";
import { Course } from "../types/employee/course";
import { PortfolioProject } from "../types/employee/portfolioProject";
import { Job } from "../types/job/job";
import { CandidateAppliedJob } from "../types/job/jobApply";

interface employeeInfoStore {
  status: "idle" | "loading" | "success" | "error";
  employeeInfo?: EmployeeInfo | null;
  setStatus: (status: "idle" | "loading" | "success" | "error") => void;
  setEmployeeInfo: (employeeInfo: EmployeeInfo | null) => void;
  clearEmployeeInfo: () => void;
}

export const useEmployeeInfoStore = create<employeeInfoStore>((set) => ({
  status: "idle",
  employeeInfo: undefined,
  setStatus: (status) => set({ status }),
  setEmployeeInfo: (employeeInfo) => set({ employeeInfo }),
  clearEmployeeInfo: () => set({ employeeInfo: null }),
}));

interface employeeDegreeStore {
  isLoading: boolean;
  employeeDegrees: Degree[];
  setIsLoading: (isLoading: boolean) => void;
  setEmployeeDegrees: (employeeDegrees: Degree[]) => void;
  clearEmployeeDegrees: () => void;
  addEmployeeDegree: (degree: Degree) => void;
  updateEmployeeDegree: (degree: Degree) => void;
  deleteEmployeeDegree: (degreeId: string) => void;
}

export const useEmployeeDegreeStore = create<employeeDegreeStore>((set) => ({
  isLoading: false,
  employeeDegrees: [],
  setIsLoading: (isLoading) => set({ isLoading }),
  setEmployeeDegrees: (employeeDegrees) => set({ employeeDegrees }),
  clearEmployeeDegrees: () => set({ employeeDegrees: [] }),
  addEmployeeDegree: (degree) =>
    set((state) => ({ employeeDegrees: [...state.employeeDegrees, degree] })),
  updateEmployeeDegree: (degree) =>
    set((state) => ({
      employeeDegrees: state.employeeDegrees.map((d) =>
        d.id === degree.id ? degree : d
      ),
    })),
  deleteEmployeeDegree: (degreeId) =>
    set((state) => ({
      employeeDegrees: state.employeeDegrees.filter((d) => d.id !== degreeId),
    })),
}));

interface employeeExperienceStore {
  isLoading: boolean;
  employeeExperiences: Experience[];
  setIsLoading: (isLoading: boolean) => void;
  setEmployeeExperiences: (employeeExperiences: Experience[]) => void;
  clearEmployeeExperiences: () => void;
  addEmployeeExperience: (experience: Experience) => void;
  updateEmployeeExperience: (experience: Experience) => void;
  deleteEmployeeExperience: (experienceId: string) => void;
}

export const useEmployeeExperienceStore = create<employeeExperienceStore>(
  (set) => ({
    isLoading: false,
    employeeExperiences: [],
    setIsLoading: (isLoading) => set({ isLoading }),
    setEmployeeExperiences: (employeeExperiences) =>
      set({ employeeExperiences }),
    clearEmployeeExperiences: () => set({ employeeExperiences: [] }),
    addEmployeeExperience: (experience) =>
      set((state) => ({
        employeeExperiences: [...state.employeeExperiences, experience],
      })),
    updateEmployeeExperience: (experience) =>
      set((state) => ({
        employeeExperiences: state.employeeExperiences.map((e) =>
          e.id === experience.id ? experience : e
        ),
      })),
    deleteEmployeeExperience: (experienceId) =>
      set((state) => ({
        employeeExperiences: state.employeeExperiences.filter(
          (e) => e.id !== experienceId
        ),
      })),
  })
);

interface employeeCertificateStore {
  isLoading: boolean;
  employeeCertificates: Certificate[];
  setIsLoading: (isLoading: boolean) => void;
  setEmployeeCertificates: (employeeCertificates: Certificate[]) => void;
  clearEmployeeCertificates: () => void;
  addEmployeeCertificate: (certificate: Certificate) => void;
  updateEmployeeCertificate: (certificate: Certificate) => void;
  deleteEmployeeCertificate: (certificateId: string) => void;
}

export const useEmployeeCertificateStore = create<employeeCertificateStore>(
  (set) => ({
    isLoading: false,
    employeeCertificates: [],
    setIsLoading: (isLoading) => set({ isLoading }),
    setEmployeeCertificates: (employeeCertificates) =>
      set({ employeeCertificates }),
    clearEmployeeCertificates: () => set({ employeeCertificates: [] }),
    addEmployeeCertificate: (certificate) =>
      set((state) => ({
        employeeCertificates: [...state.employeeCertificates, certificate],
      })),
    updateEmployeeCertificate: (certificate) =>
      set((state) => ({
        employeeCertificates: state.employeeCertificates.map((c) =>
          c.id === certificate.id ? certificate : c
        ),
      })),
    deleteEmployeeCertificate: (certificateId) =>
      set((state) => ({
        employeeCertificates: state.employeeCertificates.filter(
          (c) => c.id !== certificateId
        ),
      })),
  })
);

interface employeeCourseStore {
  isLoading: boolean;
  employeeCourses: Course[];
  setIsLoading: (isLoading: boolean) => void;
  setEmployeeCourses: (employeeCourses: Course[]) => void;
  clearEmployeeCourses: () => void;
  addEmployeeCourse: (course: Course) => void;
  updateEmployeeCourse: (course: Course) => void;
  deleteEmployeeCourse: (courseId: string) => void;
}

export const useEmployeeCourseStore = create<employeeCourseStore>((set) => ({
  isLoading: false,
  employeeCourses: [],
  setIsLoading: (isLoading) => set({ isLoading }),
  setEmployeeCourses: (employeeCourses) => set({ employeeCourses }),
  clearEmployeeCourses: () => set({ employeeCourses: [] }),
  addEmployeeCourse: (course) =>
    set((state) => ({ employeeCourses: [...state.employeeCourses, course] })),
  updateEmployeeCourse: (course) =>
    set((state) => ({
      employeeCourses: state.employeeCourses.map((c) =>
        c.id === course.id ? course : c
      ),
    })),
  deleteEmployeeCourse: (courseId) =>
    set((state) => ({
      employeeCourses: state.employeeCourses.filter((c) => c.id !== courseId),
    })),
}));

interface employeePortfolioProjectStore {
  isLoading: boolean;
  employeePortfolioProjects: PortfolioProject[];
  setIsLoading: (isLoading: boolean) => void;
  setEmployeePortfolioProjects: (
    employeePortfolioProjects: PortfolioProject[]
  ) => void;
  clearEmployeePortfolioProjects: () => void;
  addEmployeePortfolioProject: (portfolioProject: PortfolioProject) => void;
  updateEmployeePortfolioProject: (portfolioProject: PortfolioProject) => void;
  deleteEmployeePortfolioProject: (portfolioProjectId: string) => void;
}

export const useEmployeePortfolioProjectStore =
  create<employeePortfolioProjectStore>((set) => ({
    isLoading: false,
    employeePortfolioProjects: [],
    setIsLoading: (isLoading) => set({ isLoading }),
    setEmployeePortfolioProjects: (employeePortfolioProjects) =>
      set({ employeePortfolioProjects }),
    clearEmployeePortfolioProjects: () =>
      set({ employeePortfolioProjects: [] }),
    addEmployeePortfolioProject: (portfolioProject) =>
      set((state) => ({
        employeePortfolioProjects: [
          ...state.employeePortfolioProjects,
          portfolioProject,
        ],
      })),
    updateEmployeePortfolioProject: (portfolioProject) =>
      set((state) => ({
        employeePortfolioProjects: state.employeePortfolioProjects.map((p) =>
          p.id === portfolioProject.id ? portfolioProject : p
        ),
      })),
    deleteEmployeePortfolioProject: (portfolioProjectId) =>
      set((state) => ({
        employeePortfolioProjects: state.employeePortfolioProjects.filter(
          (p) => p.id !== portfolioProjectId
        ),
      })),
  }));

interface AppliedJobStatusStore {
  appliedJobs: CandidateAppliedJob[];
  page: number;
  limit: number;
  total: number;
  isLoading: boolean;
}

interface JobStatusStore {
  jobs: Job[];
  page: number;
  limit: number;
  total: number;
  isLoading: boolean;
}

export type JobStatus =
  | "applied"
  | "saved"
  | "interview"
  | "offered"
  | "rejected"
  | "hired"
  | "expired";

const defaultJobStatusStore: JobStatusStore = {
  jobs: [],
  page: 1,
  limit: 10,
  total: 0,
  isLoading: false,
};

const defaultAppliedJobStatusStore: AppliedJobStatusStore = {
  appliedJobs: [],
  page: 1,
  limit: 10,
  total: 0,
  isLoading: false,
};

interface employeeJobStore {
  jobStatusMap: Record<JobStatus, JobStatusStore>;
  appliedJobStatusMap: Record<JobStatus, AppliedJobStatusStore>;
  setPage: (status: JobStatus, page: number) => void;
  setLimit: (status: JobStatus, limit: number) => void;
  setTotal: (status: JobStatus, total: number) => void;
  setIsLoading: (status: JobStatus, isLoading: boolean) => void;
  setJobs: (status: JobStatus, jobs: Job[]) => void;
  setAppliedJobs: (
    status: JobStatus,
    appliedJobs: CandidateAppliedJob[]
  ) => void;
  clearJobs: (status: JobStatus) => void;
  addJob: (status: JobStatus, job: Job) => void;
  updateJob: (status: JobStatus, job: Job) => void;
  deleteJob: (status: JobStatus, jobId: string) => void;
  addAppliedJob: (status: JobStatus, appliedJob: CandidateAppliedJob) => void;
  updateAppliedJob: (
    status: JobStatus,
    appliedJob: CandidateAppliedJob
  ) => void;
  deleteAppliedJob: (status: JobStatus, jobId: string) => void;
}

export const useEmployeeJobStore = create<employeeJobStore>((set) => ({
  jobStatusMap: {
    applied: { ...defaultJobStatusStore },
    saved: { ...defaultJobStatusStore },
    interview: { ...defaultJobStatusStore },
    offered: { ...defaultJobStatusStore },
    rejected: { ...defaultJobStatusStore },
    hired: { ...defaultJobStatusStore },
    expired: { ...defaultJobStatusStore },
  },
  appliedJobStatusMap: {
    applied: { ...defaultAppliedJobStatusStore },
    saved: { ...defaultAppliedJobStatusStore },
    interview: { ...defaultAppliedJobStatusStore },
    offered: { ...defaultAppliedJobStatusStore },
    rejected: { ...defaultAppliedJobStatusStore },
    hired: { ...defaultAppliedJobStatusStore },
    expired: { ...defaultAppliedJobStatusStore },
  },
  setPage: (status, page) =>
    set((state) => ({
      jobStatusMap: {
        ...state.jobStatusMap,
        [status]: { ...state.jobStatusMap[status], page },
      },
      appliedJobStatusMap: {
        ...state.appliedJobStatusMap,
        [status]: { ...state.appliedJobStatusMap[status], page },
      },
    })),
  setLimit: (status, limit) =>
    set((state) => ({
      jobStatusMap: {
        ...state.jobStatusMap,
        [status]: { ...state.jobStatusMap[status], limit },
      },
      appliedJobStatusMap: {
        ...state.appliedJobStatusMap,
        [status]: { ...state.appliedJobStatusMap[status], limit },
      },
    })),
  setTotal: (status, total) =>
    set((state) => ({
      jobStatusMap: {
        ...state.jobStatusMap,
        [status]: { ...state.jobStatusMap[status], total },
      },
      appliedJobStatusMap: {
        ...state.appliedJobStatusMap,
        [status]: { ...state.appliedJobStatusMap[status], total },
      },
    })),
  setIsLoading: (status, isLoading) =>
    set((state) => ({
      jobStatusMap: {
        ...state.jobStatusMap,
        [status]: { ...state.jobStatusMap[status], isLoading },
      },
      appliedJobStatusMap: {
        ...state.appliedJobStatusMap,
        [status]: { ...state.appliedJobStatusMap[status], isLoading },
      },
    })),
  setJobs: (status, jobs) =>
    set((state) => ({
      jobStatusMap: {
        ...state.jobStatusMap,
        [status]: { ...state.jobStatusMap[status], jobs },
      },
    })),
  setAppliedJobs: (status, appliedJobs) =>
    set((state) => ({
      appliedJobStatusMap: {
        ...state.appliedJobStatusMap,
        [status]: { ...state.appliedJobStatusMap[status], appliedJobs },
      },
    })),
  clearJobs: (status) =>
    set((state) => ({
      jobStatusMap: {
        ...state.jobStatusMap,
        [status]: { ...defaultJobStatusStore },
      },
      appliedJobStatusMap: {
        ...state.appliedJobStatusMap,
        [status]: { ...defaultAppliedJobStatusStore },
      },
    })),
  addJob: (status, job) =>
    set((state) => ({
      jobStatusMap: {
        ...state.jobStatusMap,
        [status]: {
          ...state.jobStatusMap[status],
          jobs: [...state.jobStatusMap[status].jobs, job],
        },
      },
    })),
  updateJob: (status, job) =>
    set((state) => ({
      jobStatusMap: {
        ...state.jobStatusMap,
        [status]: {
          ...state.jobStatusMap[status],
          jobs: state.jobStatusMap[status].jobs.map((j) =>
            j.id === job.id ? job : j
          ),
        },
      },
    })),
  deleteJob: (status, jobId) =>
    set((state) => ({
      jobStatusMap: {
        ...state.jobStatusMap,
        [status]: {
          ...state.jobStatusMap[status],
          jobs: state.jobStatusMap[status].jobs.filter((j) => j.id !== jobId),
        },
      },
    })),
  addAppliedJob: (status, appliedJob) =>
    set((state) => ({
      appliedJobStatusMap: {
        ...state.appliedJobStatusMap,
        [status]: {
          ...state.appliedJobStatusMap[status],
          appliedJobs: [
            ...state.appliedJobStatusMap[status].appliedJobs,
            appliedJob,
          ],
        },
      },
    })),
  updateAppliedJob: (status, appliedJob) =>
    set((state) => ({
      appliedJobStatusMap: {
        ...state.appliedJobStatusMap,
        [status]: {
          ...state.appliedJobStatusMap[status],
          appliedJobs: state.appliedJobStatusMap[status].appliedJobs.map((j) =>
            j.id === appliedJob.id ? appliedJob : j
          ),
        },
      },
    })),
  deleteAppliedJob: (status, jobId) =>
    set((state) => ({
      appliedJobStatusMap: {
        ...state.appliedJobStatusMap,
        [status]: {
          ...state.appliedJobStatusMap[status],
          appliedJobs: state.appliedJobStatusMap[status].appliedJobs.filter(
            (j) => j.jobId !== jobId
          ),
        },
      },
    })),
}));
